import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Form, Table } from '@edgebox/react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalId } from '@edgebox/data-definition-kit';
import { AccessScope, AiModelModality, AiModelOptimizationType, AiModelStatus } from '@edgebox/sync-core-data-definitions';
import { type AiProviderModelDto, ClientAiModelDraft, ClientAiModelEntity, ClientAiProviderEntity } from '@edgebox/sync-core-rest-client';
import { formatDateTime, formatMoney, renderValue, useSyncCoreApi } from '../aiHelpers';
import { DetailsCard } from '../components/DetailsCard';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { RawDataCard } from '../components/RawDataCard';
import { ScreenHeader } from '../components/ScreenHeader';
import { AiModelDraftEditor, AiModelEntityEditor } from '../components/AiModelForm';
import { AiProviderName } from '../components/AiProviderName';
import { AiProviderSelector } from '../components/AiProviderSelector';

function isNewId(id: string | undefined): boolean {
  return !id || id === 'new';
}

function createDefaultModelDraft(): ClientAiModelDraft {
  const draft = new ClientAiModelDraft();
  draft.accessScope = AccessScope.Project;
  draft.status = AiModelStatus.Draft;
  draft.name = '';
  draft.useCases = [];
  draft.modelKey = '' as any;
  draft.optimizationTypes = [AiModelOptimizationType.Pretrained];
  draft.capabilities = [];
  draft.inputModalities = [AiModelModality.Text];
  draft.outputModalities = [AiModelModality.Text];
  draft.costPerInputToken = 0;
  draft.costPerOutputToken = 0;
  draft.costPerRequest = 0;
  return draft;
}

export function AiModelDetailView() {
  const api = useSyncCoreApi();
  const params = useParams();
  const navigate = useNavigate();
  const idParam = params.id;

  const creating = isNewId(idParam);
  const id = !creating ? (idParam as InternalId) : undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [entity, setEntity] = useState<ClientAiModelEntity | undefined>(undefined);
  const [provider, setProvider] = useState<ClientAiProviderEntity | undefined>(undefined);
  const [showEdit, setShowEdit] = useState<boolean>(creating);

  const [createProviderId, setCreateProviderId] = useState<InternalId | undefined>(undefined);
  const [providerModelsLoading, setProviderModelsLoading] = useState(false);
  const [providerModelsError, setProviderModelsError] = useState<unknown>(undefined);
  const [providerModels, setProviderModels] = useState<AiProviderModelDto[]>([]);
  const [prefilledDraft, setPrefilledDraft] = useState<ClientAiModelDraft | undefined>(undefined);

  const draft = useMemo(() => (creating ? createDefaultModelDraft() : undefined), [creating]);

  useEffect(() => {
    if (!api || !creating || !createProviderId) return;
    let cancelled = false;
    setProviderModelsLoading(true);
    setProviderModelsError(undefined);
    setProviderModels([]);

    api.ai.providers
      .listModels(createProviderId)
      .then((items) => {
        if (cancelled) return;
        setProviderModels(items);
      })
      .catch((e) => {
        if (cancelled) return;
        setProviderModelsError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setProviderModelsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, creating, createProviderId]);

  useEffect(() => {
    if (!api || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.models
      .item(id)
      .then(async (e) => {
        if (cancelled) return;
        setEntity(e);
        try {
          const p = await e.provider.get();
          if (!cancelled) setProvider(p);
        } catch {
          // optional
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  if (!api) return <Alert variant="warning">Missing API.</Alert>;
  if (!creating && !id) return <Alert variant="warning">Missing model id.</Alert>;

  return (
    <>
      <ScreenHeader
        title={creating ? 'Create model' : 'Model details'}
        onBack={() => navigate('/ai/model')}
        right={
          !creating && entity ? (
            <Button variant={showEdit ? 'light' : 'primary'} onClick={() => setShowEdit((v) => !v)}>
              {showEdit ? 'Cancel edit' : 'Edit'}
            </Button>
          ) : undefined
        }
      />

      {loading ? <LoadingCard text="Loading model…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      {creating && draft ? (
        <Card className="mb-3">
          <CardHeader>Model</CardHeader>
          <CardBody>
            {!prefilledDraft ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Provider</Form.Label>
                  <AiProviderSelector
                    api={api}
                    value={createProviderId}
                    disabled={providerModelsLoading}
                    onChange={(nextId) => {
                      setPrefilledDraft(undefined);
                      setCreateProviderId(nextId);
                    }}
                    name="provider"
                  />
                </Form.Group>

                {!createProviderId ? <Alert variant="info">Select a provider to list its models.</Alert> : null}

                {providerModelsLoading ? <LoadingCard text="Loading provider models…" /> : null}
                {providerModelsError ? <ErrorCard error={providerModelsError} /> : null}

                {createProviderId && !providerModelsLoading && !providerModelsError ? (
                  <>
                    <div className="fw-bold mb-2">Available models</div>
                    <Table striped bordered hover size="sm" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Model key</th>
                          <th style={{ width: 120 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {providerModels.map((m) => (
                          <tr key={m.modelKey}>
                            <td>{m.name}</td>
                            <td>{m.modelKey}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => {
                                  const next = createDefaultModelDraft();
                                  const d = m.draft;
                                  next.provider = { _: 1, id: createProviderId } as any;

                                  next.name = d.name ?? m.name;
                                  next.modelKey = (d.modelKey ?? m.modelKey) as any;
                                  next.useCases = d.useCases ?? [];
                                  next.optimizationTypes = d.optimizationTypes?.length
                                    ? d.optimizationTypes
                                    : [AiModelOptimizationType.Pretrained];
                                  next.capabilities = d.capabilities ?? [];
                                  next.inputModalities = d.inputModalities?.length ? d.inputModalities : [AiModelModality.Text];
                                  next.outputModalities = d.outputModalities?.length ? d.outputModalities : [AiModelModality.Text];

                                  next.maxInputTokens = d.maxInputTokens ?? undefined;
                                  next.maxOutputTokens = d.maxOutputTokens ?? undefined;
                                  next.contextWindowTokens = d.contextWindowTokens ?? undefined;
                                  next.costPerInputToken = d.costPerInputToken ?? next.costPerInputToken;
                                  next.costPerOutputToken = d.costPerOutputToken ?? next.costPerOutputToken;
                                  next.costPerRequest = d.costPerRequest ?? next.costPerRequest;
                                  next.versionLabel = d.versionLabel ?? undefined;

                                  setPrefilledDraft(next);
                                }}
                              >
                                Continue
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {!providerModels.length ? (
                          <tr>
                            <td colSpan={3} className="text-muted">
                              No models returned for this provider.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </>
                ) : null}
              </>
            ) : (
              <AiModelDraftEditor
                api={api}
                initialValues={prefilledDraft}
                submitLabel="Create"
                onSubmit={async (values) => {
                  const created = await api.ai.models.create(values);
                  setEntity(created);
                  navigate(`/ai/model/${created.id}`, { replace: true });
                }}
              />
            )}
          </CardBody>
        </Card>
      ) : null}

      {!creating && entity ? (
        <>
          {!showEdit ? (
            <>
              <DetailsCard
                title="Overview"
                fields={[
                  { label: 'ID', value: renderValue(entity.id) },
                  { label: 'Name', value: renderValue(entity.name) },
                  {
                    label: 'Status',
                    value: <Badge bg={entity.status === AiModelStatus.Active ? 'success' : 'secondary'}>{entity.status}</Badge>,
                  },
                  { label: 'Access scope', value: renderValue(entity.accessScope) },
                  { label: 'Frozen', value: renderValue(entity.frozen) },
                  {
                    label: 'Provider',
                    value: provider ? renderValue(provider.name) : <AiProviderName id={entity.provider?.getId?.()} />,
                  },
                  { label: 'Model key', value: renderValue(entity.modelKey) },
                  { label: 'Use cases', value: renderValue(entity.useCases) },
                  { label: 'Customer', value: renderValue(entity.customer?.getId?.()) },
                  { label: 'Project', value: renderValue(entity.project?.getId?.()) },
                  { label: 'Created', value: renderValue(formatDateTime(entity.createdAt)) },
                  { label: 'Updated', value: renderValue(formatDateTime(entity.updatedAt)) },
                ]}
              />

              <DetailsCard
                title="Costs"
                fields={[
                  {
                    label: 'Cost / 1M input tokens',
                    value: renderValue(
                      formatMoney(entity.costPerInputToken !== undefined ? entity.costPerInputToken * 1_000_000 : undefined)
                    ),
                  },
                  {
                    label: 'Cost / 1M output tokens',
                    value: renderValue(
                      formatMoney(entity.costPerOutputToken !== undefined ? entity.costPerOutputToken * 1_000_000 : undefined)
                    ),
                  },
                  {
                    label: 'Cost / 1K requests',
                    value: renderValue(formatMoney(entity.costPerRequest !== undefined ? entity.costPerRequest * 1_000 : undefined)),
                  },
                ]}
              />

              <RawDataCard entity={entity} />
            </>
          ) : (
            <Card className="mb-3">
              <CardHeader>Edit model</CardHeader>
              <CardBody>
                <AiModelEntityEditor
                  api={api}
                  initialValues={entity}
                  submitLabel="Save"
                  onSubmit={async (values) => {
                    const updated = await api.ai.models.update(values);
                    setEntity(updated);
                    setShowEdit(false);
                  }}
                />
              </CardBody>
            </Card>
          )}
        </>
      ) : null}
    </>
  );
}
